import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { StatementSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api, ApiRequestError } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const ledgerStatementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/statements',
  component: LedgerStatementsPage,
});

type UploadForm = {
  accountId: string;
  bankTemplateId: string;
};

type UploadError = { title: string; message: string; foundColumns?: string[] };

type StatementSortKey = 'filename' | 'account' | 'range' | 'rows' | 'status';
const EMPTY_STATEMENTS: StatementSummary[] = [];

/**
 * A parse failure means the file's layout is not the one the chosen template
 * describes. The server's message names the column it could not find; this
 * turns that into "wrong template" guidance and lists what the file does have.
 */
function describeUploadError(err: unknown, templateName: string | undefined): UploadError {
  if (err instanceof ApiRequestError && err.details.rule === 'statement.parse_error') {
    const found = Array.isArray(err.details.headers)
      ? (err.details.headers as unknown[]).map(String).filter((h) => h.trim() !== '')
      : undefined;
    const template = templateName ? `the "${templateName}" template` : 'the selected bank template';
    return {
      title: 'File does not match the bank template',
      message: `This file is not laid out the way ${template} expects (${err.message}). Pick the template for this bank, or create one under Ledger → Bank Templates.`,
      foundColumns: found,
    };
  }
  return { title: 'Upload failed', message: toMessage(err) };
}

function statusBadge(status: string): JSX.Element {
  const variant =
    status === 'posted' ? 'default' : status === 'in_review' ? 'muted' : status === 'parsed' ? 'muted' : 'muted';
  return <Badge variant={variant as 'default' | 'muted'}>{status}</Badge>;
}

function LedgerStatementsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<UploadError | null>(null);
  const [duplicateNotice, setDuplicateNotice] = React.useState<{ fileId: string; existingStatus: string } | null>(null);
  const [file, setFile] = React.useState<File | null>(null);

  const listQuery = useQuery({
    queryKey: ['statements'],
    queryFn: () => api.listStatements(),
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  });
  const templatesQuery = useQuery({
    queryKey: ['bank-templates'],
    queryFn: () => api.listBankTemplates(),
  });

  const sortAccessors = React.useMemo<SortAccessors<StatementSummary, StatementSortKey>>(
    () => ({
      filename: (s) => s.filename,
      account: (s) => accountsQuery.data?.find((a) => a.id === s.accountId)?.name ?? s.accountId,
      range: (s) => s.fromDate,
      rows: (s) => s.rowCounts.pending + s.rowCounts.suggested + s.rowCounts.confirmed + s.rowCounts.skipped,
      status: (s) => s.status,
    }),
    [accountsQuery.data],
  );
  const { rows: statements, ...sorter } = useSortedRows(listQuery.data ?? EMPTY_STATEMENTS, sortAccessors);

  const form = useForm<UploadForm>({
    defaultValues: { accountId: '', bankTemplateId: '' },
  });

  const uploadMutation = useMutation({
    mutationFn: async (values: UploadForm) => {
      if (!file) throw new Error('Pick a file first.');
      return api.uploadStatement(values.accountId, values.bankTemplateId, file);
    },
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['statements'] });
      if (res.status === 'duplicate') {
        setDuplicateNotice({ fileId: res.fileId, existingStatus: res.existingStatus });
        return;
      }
      setDialogOpen(false);
      setFile(null);
      form.reset();
      setUploadError(null);
      setDuplicateNotice(null);
      await navigate({ to: '/ledger/statements/$fileId', params: { fileId: res.fileId } });
    },
    onError: (err, values) => {
      const template = templatesQuery.data?.find((t) => t.id === values.bankTemplateId);
      setUploadError(describeUploadError(err, template?.name));
    },
  });

  const closeDialog = (): void => {
    setDialogOpen(false);
    setUploadError(null);
    setDuplicateNotice(null);
  };

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 lg:flex-nowrap">
        <div className="min-w-0">
          <h1 className="font-display text-3xl">Statements</h1>
          <p className="text-muted-foreground mt-1">Upload a bank statement, review the rows, then post.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>Upload statement</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All statements</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : listQuery.data && listQuery.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] text-sm [&_td]:pr-3 [&_th]:pr-3">
                <thead>
                  <tr className="border-b text-left">
                    <SortableTh sortKey="filename" controller={sorter} className="py-2">
                      Filename
                    </SortableTh>
                    <SortableTh sortKey="account" controller={sorter} className="py-2">
                      Account
                    </SortableTh>
                    <SortableTh sortKey="range" controller={sorter} className="py-2">
                      Range
                    </SortableTh>
                    <SortableTh sortKey="rows" controller={sorter} className="py-2">
                      Rows
                    </SortableTh>
                    <SortableTh sortKey="status" controller={sorter} className="py-2">
                      Status
                    </SortableTh>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((s) => {
                    const acct = accountsQuery.data?.find((a) => a.id === s.accountId);
                    return (
                      <tr key={s.id} className="border-b">
                        <td className="max-w-[16rem] break-all py-2">{s.filename}</td>
                        <td className="py-2">{acct?.name ?? s.accountId}</td>
                        <td className="py-2">
                          <span className="whitespace-nowrap">{s.fromDate ?? '?'}</span> →{' '}
                          <span className="whitespace-nowrap">{s.toDate ?? '?'}</span>
                        </td>
                        <td className="py-2">
                          <span title="pending / suggested / confirmed / skipped">
                            {s.rowCounts.pending}p · {s.rowCounts.suggested}s · {s.rowCounts.confirmed}c ·{' '}
                            {s.rowCounts.skipped}k
                          </span>
                        </td>
                        <td className="py-2">{statusBadge(s.status)}</td>
                        <td className="py-2 text-right">
                          <Link to="/ledger/statements/$fileId" params={{ fileId: s.id }}>
                            <Button size="sm" variant="ghost">
                              Open
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground">No statements uploaded yet.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload statement</DialogTitle>
            <DialogDescription>
              Pick the account and the bank template that matches this file's layout, then attach the file.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => uploadMutation.mutate(v))} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="accountId">Account</Label>
              <select
                id="accountId"
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                {...form.register('accountId', { required: true })}
              >
                <option value="">Select…</option>
                {accountsQuery.data
                  ?.filter((a) => a.active)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.numberMasked})
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bankTemplateId">Bank template</Label>
              <select
                id="bankTemplateId"
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                {...form.register('bankTemplateId', { required: true })}
              >
                <option value="">Select…</option>
                {templatesQuery.data
                  ?.filter((t) => t.active)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="file">File</Label>
              <input
                id="file"
                type="file"
                accept=".csv,.xlsx,.pdf"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setUploadError(null);
                  setDuplicateNotice(null);
                }}
                className="block w-full rounded-md border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[hsl(var(--muted))] file:px-3 file:py-1 file:text-sm file:font-medium"
              />
              <p className="text-xs text-[hsl(var(--muted-foreground))]">CSV, XLSX or PDF, up to 25 MB.</p>
            </div>
            {duplicateNotice && (
              <Alert>
                <AlertTitle>Already uploaded</AlertTitle>
                <AlertDescription>
                  This exact file (matched by SHA-256) was already uploaded — status{' '}
                  <strong>{duplicateNotice.existingStatus}</strong>.{' '}
                  <Link to="/ledger/statements/$fileId" params={{ fileId: duplicateNotice.fileId }}>
                    Open existing
                  </Link>
                </AlertDescription>
              </Alert>
            )}
            {uploadError && (
              <Alert variant="destructive">
                <AlertTitle>{uploadError.title}</AlertTitle>
                <AlertDescription>
                  <p>{uploadError.message}</p>
                  {uploadError.foundColumns && uploadError.foundColumns.length > 0 ? (
                    <p className="mt-2">
                      Columns found in the file:{' '}
                      <span className="font-mono text-xs">{uploadError.foundColumns.join(' · ')}</span>
                    </p>
                  ) : null}
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploadMutation.isPending || !file}>
                {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
